import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";

import type { SliceStateCreator } from "../types";

interface ChatThreadsState {
  chatMessages: any[];
  firstMessageId: number | null;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  isLoading: boolean;
  lastMessageId: number | null;
  metaData: any;
  scrollInfo: any;
  totalMessagesCount: number;
}

interface ChatThreadsActions {
  appendLastMessage: (message: any) => void;
  clearSpotlightedMessages: () => void;
  setChatMessages: (messages: any[]) => void;
  setFirstMessageId: (id: number | null) => void;
  setHasNextPage: (hasNextPage: boolean) => void;
  setHasPrevPage: (hasPrevPage: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setLastMessageId: (id: number | null) => void;
  setMetaData: (metaData: any) => void;
  setScrollInfo: (scrollInfo: any) => void;
  setTotalMessagesCount: (totalMessagesCount: number) => void;
  updateMessage: (
    message: Pick<ChatRoomMessage, "id"> & Partial<ChatRoomMessage>,
  ) => void;
}

export interface ChatThreadsSliceState {
  threads: ChatThreadsState & ChatThreadsActions;
}

const initialChatThreadsState: ChatThreadsState = {
  chatMessages: [],
  isLoading: false,
  hasNextPage: false,
  hasPrevPage: false,
  scrollInfo: {},
  firstMessageId: null,
  lastMessageId: null,
  totalMessagesCount: 0,
  metaData: {},
};

export const createThreadsSlice: SliceStateCreator<
  ChatThreadsSliceState
> = set => ({
  threads: {
    ...initialChatThreadsState,
    setChatMessages: messages =>
      set(state => {
        state.threads.chatMessages = messages;
      }),
    setIsLoading: isLoading =>
      set(state => {
        state.threads.isLoading = isLoading;
      }),
    setHasNextPage: hasNextPage =>
      set(state => {
        state.threads.hasNextPage = hasNextPage;
      }),
    setHasPrevPage: hasPrevPage =>
      set(state => {
        state.threads.hasPrevPage = hasPrevPage;
      }),
    setScrollInfo: scrollInfo =>
      set(state => {
        state.threads.scrollInfo = scrollInfo;
      }),
    setFirstMessageId: id =>
      set(state => {
        state.threads.firstMessageId = id;
      }),
    setLastMessageId: id =>
      set(state => {
        state.threads.lastMessageId = id;
      }),
    setTotalMessagesCount: totalMessagesCount =>
      set(state => {
        state.threads.totalMessagesCount = totalMessagesCount;
      }),
    setMetaData: metaData =>
      set(state => {
        state.threads.metaData = metaData;
      }),
    clearSpotlightedMessages: () =>
      set(state => {
        state.threads.chatMessages = state.threads.chatMessages.map(
          message => ({
            ...message,
            spotlighted: false,
          }),
        );
      }),
    updateMessage: message =>
      set(state => {
        const index = state.threads.chatMessages.findIndex(
          (currentMessage: ChatRoomMessage) => currentMessage.id === message.id,
        );
        if (index > -1) {
          state.threads.chatMessages[index] = {
            ...state.threads.chatMessages[index],
            ...message,
          };
        }
      }),
    appendLastMessage: (message: any) =>
      set(state => {
        const currentMessages = state.threads.chatMessages;

        const indexOfMessageWithSameUUID = currentMessages.findIndex(
          (m: any) => m.creation_uuid === message.creation_uuid,
        );

        if (indexOfMessageWithSameUUID > -1) {
          if (message.id > 0) {
            const updatedMessages = [...currentMessages];
            updatedMessages[indexOfMessageWithSameUUID] = message;
            state.threads.chatMessages = updatedMessages;
          }
        } else {
          state.threads.chatMessages = [...currentMessages, message];
        }
      }),
  },
});
